import React, {useState} from "react"
import {Box, Button, Grid, Paper, TextField, Typography} from "@mui/material"
import styled from "styled-components"

import {api, Note} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {linkifyText} from "@indebted/components/safeHTML"
import {useAgent} from "@indebted/hooks/Agent"

const localTimeOptions = {
	year: "numeric",
	month: "short",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	hour12: true,
	timeZoneName: "short",
} as const

function toLocalTime(epoch: number) {
	return new Intl.DateTimeFormat("default", localTimeOptions).format(new Date(epoch))
}

function NoteItemCard({note}: {note: Note}) {
	return (
		<Paper elevation={2}>
			<Box p={4}>
				<Grid direction="column" container spacing={2}>
					<Grid size={12} container justifyContent="space-between" alignItems="center">
						<Grid size="auto">
							<Typography color="textSecondary" variant="subtitle2">
								<time>{toLocalTime(note.CreatedAt)}</time>
							</Typography>
						</Grid>
						<Grid size="auto">
							<Typography color="textSecondary" variant="subtitle2">
								{note.AgentName}
							</Typography>
						</Grid>
					</Grid>

					<Grid size={12}>
						<NoteTypography variant="body2" dangerouslySetInnerHTML={{__html: linkifyText(note.Body)}} />
					</Grid>
				</Grid>
			</Box>
		</Paper>
	)
}

const NoteTypography = styled(Typography)`
	white-space: break-spaces;

	a {
		word-break: break-all;
	}
`

function Notes({
	customerID,
	notes,
	reload,
	systemOfRecord,
}: {
	customerID: string
	notes: Note[]
	reload: () => void
	systemOfRecord: string
}) {
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const [noteBody, setNoteBody] = useState<string | undefined>()

	const handleAddNote = (noteBody: string) => {
		api.customer
			.addNote(customerID, {
				Body: noteBody,
				AgentEmail: agentEmail,
				AgentName: agentName,
			})
			.then(() => {
				notification.success(
					`Note added successfully. Please wait up to 48 hours for propagation in all systems.`,
				)

				setNoteBody("")
				setTimeout(reload, 500)
			})
			.catch((error) => {
				notification.error(error.Message)
			})
	}

	const isLatitude = systemOfRecord === "Latitude"

	return (
		<Box p={3}>
			<Grid direction="column" spacing={2} container>
				<Grid size={12}>
					<TextField
						multiline
						minRows={4}
						maxRows={50}
						placeholder={
							isLatitude ? "Notes are disabled for Latitude customers" : "Add a note about the customer"
						}
						variant="outlined"
						fullWidth
						value={noteBody}
						onChange={(el) => setNoteBody(el.target.value)}
						disabled={isLatitude}
					/>
				</Grid>
				<Grid size={12}>
					<Grid container justifyContent="flex-end" spacing={2}>
						<Grid size="auto">
							<Button disabled={!noteBody || isLatitude} onClick={() => handleAddNote(noteBody!)}>
								Add Note
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Typography variant="subtitle1">{notes.length === 0 ? "No notes" : "Notes"}</Typography>
			<Grid direction="column" spacing={2} container>
				{notes.map((note, idx) => (
					<Grid key={idx} size={12}>
						<NoteItemCard note={note} />
					</Grid>
				))}
			</Grid>
		</Box>
	)
}

export {Notes}
